import { useState } from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRightLong, faBook, faBoltLightning, faChartLine, faGraduationCap,
    faHeadset, faLinesLeaning, faPencilRuler, faPlugCircleCheck, faShieldHalved,
    faTabletScreenButton, faTriangleExclamation, faLightbulb, faChevronDown
} from '@fortawesome/free-solid-svg-icons';

import HeaderHome from '../../Components/Header_Home/Header_Home';
import {
    Reveal, RevealText, Parallax, Counter, Track,
    ScrollProgress, Scene, Spotlight, Magnetic,
} from '../../Components/Motion/Motion';
import ZenLogoWhite from "../../imgs/ZenLogo_white.svg";
import ipadMockup from "../../imgs/ipad-photo.webp";
import './Home.css';

// One entry per module. `group` decides the column it sits in and the photo
// behind the preview pane; the long text is what the preview shows.
interface ModuleDef {
    id: string;
    group: "high" | "low";
    icon: any;
    ro: string; en: string;
    roDesc: string; enDesc: string;
}

const MODULES: ModuleDef[] = [
    {
        id: "learning", group: "high", icon: faLinesLeaning,
        ro: "Învățare", en: "Learning",
        roDesc: "Dispecerul dictează manevra prin voce, exact ca în exploatare. O confirmi, apoi o execuți pe schema stației. Comenzile se reiau până intră în reflex.",
        enDesc: "The dispatcher dictates the maneuver by voice, exactly as in the field. You read it back, then perform it on the station scheme. Commands repeat until they become reflex.",
    },
    {
        id: "training", group: "high", icon: faBook,
        ro: "Antrenare", en: "Training",
        roDesc: "Manevre pe simulatorul de stație, fără dictare. Antrenamentul repetat pe simulator este cea mai directă cale de a reduce gradul de eroare umană.",
        enDesc: "Maneuvers on the station simulator, undictated. Repeated simulator practice is the most direct way to cut down human error.",
    },
    {
        id: "testing", group: "high", icon: faGraduationCap,
        ro: "Testare", en: "Testing",
        roDesc: "Ești punctat după corectitudinea manevrelor. Simulatorul redă ambele regimuri din stație: funcționare normală, după foi de manevră standard, și regim de avarie, fără foi.",
        enDesc: "You are scored on how correctly the maneuvers were executed. The simulator reproduces both station regimes: normal operation following standard maneuver sheets, and emergency operation without them.",
    },
    {
        id: "edit", group: "low", icon: faPencilRuler,
        ro: "Editare", en: "Edit",
        roDesc: "Creezi o schemă electrică de la zero și o printezi în A4 / A3. Conturi separate pentru elevi și profesori.",
        enDesc: "Draw an electrical scheme from scratch and print it in A4 / A3. Separate accounts for students and teachers.",
    },
    {
        id: "functional", group: "low", icon: faLightbulb,
        ro: "Funcționare", en: "Functional",
        roDesc: "Scheme predefinite arătate pas cu pas: aprinderea unui bec, pornirea unui motor asincron trifazat, inversorul de sens, pornirea stea-triunghi.",
        enDesc: "Predefined schemes shown step by step: lighting a bulb, starting a three-phase asynchronous motor, the reversing starter, star-delta starting.",
    },
    {
        id: "debugging", group: "low", icon: faPlugCircleCheck,
        ro: "Depanare", en: "Debug",
        roDesc: "Profesorul introduce defectul, elevul îl caută cu aparatul de măsură virtual: cu tensiune 200 / 700 VCA, fără tensiune cu buzer sau cu megohmetru.",
        enDesc: "The teacher plants the fault, the student tracks it down with the virtual measuring device: live at 200 / 700 VAC, dead with the buzzer or the megohmmeter.",
    },
];

const Home = ({ data } : { data : any }) => {

    const [cookies] = useCookies();
    const en = cookies?.language === "en";
    const navigate = useNavigate();

    const foundUser = data?.userData != null;
    const [activeModule, setActiveModule] = useState<string>("learning");
    const active = MODULES.find((m) => m.id === activeModule) || MODULES[0];

    const goToApp = () => navigate(foundUser ? "/categories" : "/login");
    const ctaLabel = foundUser ? (en ? "Towards app" : "Spre aplicație") : "Login";

    // Both halves of the platform get equal billing: three modules each.
    const stats = [
        { value: "6", ro: "module de lucru", en: "working modules" },
        { value: "3", ro: "module de joasă tensiune", en: "low-tension modules" },
        { value: "3", ro: "module de înaltă tensiune", en: "high-tension modules" },
        { value: "0", ro: "risc în teren", en: "risk in the field" },
    ];

    // Low tension first, station second, assessment for both.
    const steps = [
        {
            icon: faPencilRuler,
            ro: "Desenezi și încerci schema", en: "You draw the scheme and try it",
            roDesc: "La joasă tensiune pornești de la zero: construiești schema în editor, o vezi funcționând pas cu pas și cauți defectul cu aparatul de măsură.",
            enDesc: "At low tension you start from scratch: build the scheme in the editor, watch it work step by step and track the fault with the measuring device.",
        },
        {
            icon: faHeadset,
            ro: "Treci la schema stației", en: "You move up to the station scheme",
            roDesc: "La înaltă tensiune dispecerul dictează manevra prin voce, în formularea din exploatare. O confirmi, apoi o execuți pe schema stației.",
            enDesc: "At high tension the dispatcher dictates the maneuver by voice, worded as it is in the field. You read it back, then perform it on the station scheme.",
        },
        {
            icon: faChartLine,
            ro: "Primești punctaj", en: "You get scored",
            roDesc: "Și depanarea, și manevra sunt verificate. Clasamentul arată unde s-au pierdut puncte și ce merită reluat.",
            enDesc: "Both the fault-finding and the maneuver are checked. The leaderboard shows where points were lost and what is worth repeating.",
        },
    ];

    // Two on low tension, two on the station.
    const benefits = [
        {
            icon: faLightbulb,
            ro: "De la desen la funcționare", en: "From drawing to operation",
            roDesc: "Construiești schema în editor, o printezi în A4 / A3 și o vezi funcționând pas cu pas: aprinderea unui bec, motorul asincron, inversorul de sens, stea-triunghi.",
            enDesc: "Build the scheme in the editor, print it in A4 / A3 and watch it work step by step: lighting a bulb, the asynchronous motor, the reversing starter, star-delta.",
        },
        {
            icon: faPlugCircleCheck,
            ro: "Depanare cu aparatul de măsură", en: "Fault-finding with the measuring device",
            roDesc: "Profesorul introduce defectul, elevul îl caută cu aparatul virtual: cu tensiune 200 / 700 VCA, fără tensiune cu buzer sau cu megohmetru.",
            enDesc: "The teacher plants the fault, the student tracks it down with the virtual device: live at 200 / 700 VAC, dead with the buzzer or the megohmmeter.",
        },
        {
            icon: faShieldHalved,
            ro: "Manevre reluate fără urmări", en: "Maneuvers repeated without consequence",
            roDesc: "Manevra greșită se oprește în simulator. Poate fi reluată de zece ori, în aceleași condiții, fără efect în teren.",
            enDesc: "A wrong maneuver stops inside the simulator. It can be repeated ten times over, under the same conditions, with no effect in the field.",
        },
        {
            icon: faTriangleExclamation,
            ro: "Regim normal și regim de avarie", en: "Normal and emergency regimes",
            roDesc: "Se exersează atât manevrele după foi standard, cât și situațiile de avarie, unde foaia de manevră lipsește și decizia e a operatorului.",
            enDesc: "Both maneuvers by standard sheets and emergency situations are practised — the ones where there is no sheet and the call is the operator's.",
        },
    ];

    const moduleColumn = (group: "high" | "low") => (
        <Reveal
            as="div"
            className="hp-mod-group"
            from={group === "high" ? "left" : "right"}
            delay={group === "high" ? 0 : 120}
        >
            <p className={`hp-kicker ${group}`}>
                <span className="hp-kicker-dot" />
                {group === "high"
                    ? (en ? "High tension" : "Înaltă tensiune")
                    : (en ? "Low tension" : "Joasă tensiune")}
            </p>

            {MODULES.filter((m) => m.group === group).map((m) => (
                <Spotlight
                    key={m.id}
                    as="button"
                    type="button"
                    className={`hp-mod-card${activeModule === m.id ? " active" : ""}`}
                    aria-pressed={activeModule === m.id}
                    onClick={() => setActiveModule(m.id)}
                >
                    <span className="hp-mod-icon"><FontAwesomeIcon icon={m.icon} /></span>
                    <span className="hp-mod-name">{en ? m.en : m.ro}</span>
                    <span className="hp-mod-go"><FontAwesomeIcon icon={faArrowRightLong} /></span>
                </Spotlight>
            ))}
        </Reveal>
    );

    return (
        <div className="home-container home-page">
            <ScrollProgress />
            <HeaderHome data={data} activePage="home" />

            {/* Two big blurred blobs drifting behind the whole page. */}
            <div className="hp-aurora" aria-hidden="true">
                <span className="hp-blob a" />
                <span className="hp-blob b" />
            </div>

            <div className="content">

                {/* ---- hero: pinned while the headline recedes ---- */}
                <Scene className="hp-hero-scene" length={.85}>
                    <section className="hp-hero">
                        <div className="hp-hero-text">
                            <Reveal as="p" className="hp-kicker">
                                <span className="hp-kicker-dot" />
                                {en ? "Low and high tension" : "Joasă și înaltă tensiune"}
                            </Reveal>

                            <RevealText
                                as="h1"
                                className="hp-title"
                                entrance
                                lines={en
                                    ? [[{ text: "Low tension and high tension," }], [{ text: "in one place.", accent: true }]]
                                    : [[{ text: "Joasă și înaltă tensiune," }], [{ text: "într-un singur loc.", accent: true }]]}
                            />

                            <Reveal as="p" className="hp-lead" delay={160}>
                                {en
                                    ? "ZenVolt covers both levels. At low tension you draw a scheme, watch it work and find the fault with the measuring device. At high tension you run maneuvers on the station scheme — all of it in the browser."
                                    : "ZenVolt acoperă ambele niveluri. La joasă tensiune desenezi schema, o vezi funcționând și cauți defectul cu aparatul de măsură. La înaltă tensiune execuți manevre pe schema stației — totul în browser."}
                            </Reveal>

                            <Reveal className="hp-cta-row" delay={280}>
                                <Magnetic strength={8}>
                                    <button type="button" className="hp-cta" onClick={goToApp}>
                                        {ctaLabel} <FontAwesomeIcon icon={faArrowRightLong} />
                                    </button>
                                </Magnetic>
                                <Magnetic strength={6}>
                                    <a className="hp-cta ghost" href="#module">
                                        {en ? "See the modules" : "Vezi modulele"}
                                    </a>
                                </Magnetic>
                            </Reveal>
                        </div>

                        {/* The station photo behind a pane of glass — `track` keeps
                            publishing scroll progress, so the photo drifts inside
                            the frame long after the entrance has played. */}
                        <Reveal className="hp-hero-visual" from="zoom" delay={140} track>
                            <img className="hp-hero-logo" src={ZenLogoWhite} alt="ZenVolt" />
                            <div className="hp-hero-caption">
                                <span className="hp-hero-chip">
                                    <FontAwesomeIcon icon={faBoltLightning} />
                                    {en ? "Station one-line diagram" : "Schema monofilară a stației"}
                                </span>
                                <p>
                                    {en
                                        ? "The same one-line diagram you meet on shift — disconnectors, breakers, earthing switches."
                                        : "Aceeași schemă monofilară pe care o ai în tură — separatoare, întrerupătoare, CLP-uri."}
                                </p>
                            </div>
                        </Reveal>
                    </section>

                    <span className="hp-scroll-cue" aria-hidden="true">
                        <FontAwesomeIcon icon={faChevronDown} />
                    </span>
                </Scene>

                {/* ---- numbers ---- */}
                <section className="hp-stats">
                    {stats.map((s, i) => (
                        <Reveal key={s.en} delay={i * 90}>
                            <Spotlight className="hp-stat">
                                <Counter value={s.value} />
                                <span>{en ? s.en : s.ro}</span>
                            </Spotlight>
                        </Reveal>
                    ))}
                </section>

                {/* ---- how a maneuver runs ---- */}
                <section className="hp-section hp-split">
                    {/* The heading parks itself while the three steps scroll past it. */}
                    <header className="hp-section-head hp-head-sticky">
                        <Reveal as="p" className="hp-kicker">
                            <span className="hp-kicker-dot" />
                            {en ? "How it runs" : "Cum decurge"}
                        </Reveal>
                        <RevealText
                            className="hp-h2"
                            lines={en
                                ? [[{ text: "From the scheme," }, { text: "to the station", accent: true }]]
                                : [[{ text: "De la schemă," }, { text: "până la stație", accent: true }]]}
                        />

                        {/* Fills as the section is scrolled through. */}
                        <Track className="hp-rail" start={.95} end={.35}>
                            <span className="hp-rail-fill" />
                        </Track>
                    </header>

                    <div className="hp-steps">
                        {steps.map((s, i) => (
                            <Reveal key={s.en} delay={i * 110}>
                                <Spotlight as="article" className="hp-step">
                                    <span className="hp-step-no">{`0${i + 1}`}</span>
                                    <span className="hp-step-icon"><FontAwesomeIcon icon={s.icon} /></span>
                                    <h3>{en ? s.en : s.ro}</h3>
                                    <p>{en ? s.enDesc : s.roDesc}</p>
                                </Spotlight>
                            </Reveal>
                        ))}
                    </div>
                </section>

                {/* ---- modules ---- */}
                <section className="hp-section" id="module">
                    <header className="hp-section-head">
                        <Reveal as="p" className="hp-kicker">
                            <span className="hp-kicker-dot" />
                            {en ? "Modules" : "Module"}
                        </Reveal>
                        <RevealText
                            className="hp-h2"
                            lines={en
                                ? [[{ text: "Six modules," }, { text: "one platform", accent: true }]]
                                : [[{ text: "Șase module," }, { text: "o singură platformă", accent: true }]]}
                        />
                    </header>

                    <div className="modules-container hp-modules">
                        <Reveal
                            from="zoom"
                            delay={60}
                            className={`hp-mod-preview ${active.group === "high" ? "high-tension" : "low-tension"} ${active.id}`}
                        >
                            <div className="hp-mod-preview-caption">
                                <p className="hp-mod-preview-tag">
                                    {active.group === "high"
                                        ? (en ? "High tension" : "Înaltă tensiune")
                                        : (en ? "Low tension" : "Joasă tensiune")}
                                </p>
                                <h3>{en ? active.en : active.ro}</h3>
                                <p>{en ? active.enDesc : active.roDesc}</p>
                                <Magnetic strength={7}>
                                    <button type="button" className="hp-cta small" onClick={goToApp}>
                                        {en ? "Open the module" : "Deschide modulul"} <FontAwesomeIcon icon={faArrowRightLong} />
                                    </button>
                                </Magnetic>
                            </div>
                        </Reveal>

                        <div className="hp-mod-groups">
                            {moduleColumn("high")}
                            {moduleColumn("low")}
                        </div>
                    </div>
                </section>

                {/* ---- why ---- */}
                <section className="hp-section">
                    <header className="hp-section-head">
                        <Reveal as="p" className="hp-kicker">
                            <span className="hp-kicker-dot" />
                            {en ? "Why the simulator" : "De ce simulatorul"}
                        </Reveal>
                        <RevealText
                            className="hp-h2"
                            lines={en
                                ? [[{ text: "The same schemes," }, { text: "without the consequences", accent: true }]]
                                : [[{ text: "Aceleași scheme," }, { text: "fără consecințe", accent: true }]]}
                        />
                    </header>

                    <div className="hp-benefits">
                        {benefits.map((b, i) => (
                            <Reveal
                                key={b.en}
                                from={i % 2 === 0 ? "left" : "right"}
                                delay={(i % 2) * 90}
                            >
                                <Spotlight as="article" className="hp-benefit">
                                    <span className="hp-benefit-icon"><FontAwesomeIcon icon={b.icon} /></span>
                                    <h3>{en ? b.en : b.ro}</h3>
                                    <p>{en ? b.enDesc : b.roDesc}</p>
                                </Spotlight>
                            </Reveal>
                        ))}
                    </div>
                </section>

                {/* ---- tablet band ---- */}
                <section className="hp-band">
                    <div className="hp-band-text">
                        <Reveal as="p" className="hp-kicker" from="left">
                            <span className="hp-kicker-dot" />
                            <FontAwesomeIcon icon={faTabletScreenButton} />
                            {en ? "In the field too" : "Și în teren"}
                        </Reveal>
                        <RevealText
                            className="hp-h2"
                            lines={en
                                ? [[{ text: "The whole lab" }, { text: "on a tablet", accent: true }]]
                                : [[{ text: "Tot laboratorul" }, { text: "pe tabletă", accent: true }]]}
                        />
                        <Reveal as="p" className="hp-lead" from="left" delay={140}>
                            {en
                                ? "Nothing to install: both the scheme editor and the station simulator run in the browser, on a laptop in the classroom or on a tablet next to the panel."
                                : "Nimic de instalat: și editorul de scheme, și simulatorul de stație rulează în browser, pe laptop la clasă sau pe tabletă, lângă panou."}
                        </Reveal>
                    </div>

                    {/* Drifts against the scroll, so the tablet floats a little. */}
                    <Parallax className="hp-band-visual" distance={64}>
                        <img src={ipadMockup} alt={en ? "ZenVolt on a tablet" : "ZenVolt pe tabletă"} />
                    </Parallax>
                </section>

                {/* ---- closing ---- */}
                <section className="hp-final">
                    <Reveal from="zoom" className="hp-final-logo-wrap">
                        <img src={ZenLogoWhite} alt="ZenVolt" className="hp-final-logo" width={90} height="auto" />
                    </Reveal>

                    <RevealText
                        className="hp-final-title"
                        start={.88}
                        end={.5}
                        lines={en
                            ? [[{ text: "From your first scheme", accent: true }], [{ text: "to the maneuvers in the station." }]]
                            : [[{ text: "De la prima schemă", accent: true }], [{ text: "până la manevrele din stație." }]]}
                    />

                    <Reveal delay={220}>
                        <Magnetic strength={12}>
                            <button type="button" className="hp-cta light" onClick={goToApp}>
                                {ctaLabel} <FontAwesomeIcon icon={faArrowRightLong} />
                            </button>
                        </Magnetic>
                    </Reveal>

                    <p className="hp-final-meta">© {new Date().getFullYear()}. All rights reserved.</p>
                    <p className="hp-final-meta">{en ? "Created by Barbu Mihai." : "Creat de Barbu Mihai."}</p>
                </section>

            </div>
        </div>
    );
};

export default Home;
